/* eslint-disable react/prop-types */
import { useState } from "react";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { postJobApplicationFavoriteEmail } from "src/redux/userAction/applyFavEmail/action";
import { connect } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { jobDetailType, reqObjType } from "src/components/common/MainContent/JobResults/common";
import { IconButton, Tooltip } from "@material-ui/core";
import clsx from "clsx";
import MailOutlinedIcon from "@material-ui/icons/MailOutlined";
import MailIcon from "@material-ui/icons/Mail";
import Analytics from "src/services/Analytics";
import eligibilityPercentageCalc from "src/components/common/EligibilityMeter/EligibilityPercentage";
import styles from "./EmailAndFavoriteIcons.module.scss";
import ControlledEmailTooltip from "../../../JobResults/ControlledEmailTooltip";

// Define explicit props with strict typing
export interface EmailAndFavoriteIconsProps {
  // recieve from parent
  showContactAction?: boolean;
  showFavoriteAction?: boolean;
  recommended?: boolean;
  isRecommendedJobsOnResult?: boolean;
  sourceType: string;
  campaignCode: string;
  job: jobDetailType;
  viewMyJobs?: boolean;
  similarOrViewed?: string;
  showIconsInPrimaryColor?: boolean;
  onEmailClick: () => void;
  // recieve from redux store
  actionJobId: string;
  error: boolean;
  emailSuccess: boolean;
  emailSuccessResponse: {
    appliedJobId: string;
    message: string;
    status: number;
    statusCode: string;
  };
  allJobs: any;
  jobDetails: any;
  similarJobs: any;
  collaboratedJobs: any;
  myJobs: any;
  recommendedJobs: any;
  postTheJobApplicationFavoriteEmail(reqObj: reqObjType & { jobId: string }): void;
}

const trackSegmentEvent = (
  eventName: string,
  props: { job?: any; sourceType?: any; campaignCode?: any }
) => {
  const { job } = props;

  const segmentEventName = eventName;
  const segmentEventProps = {
    jobId: job?.atsJobId,
    origin: document.referrer,
    profession: job?.professionCode,
    specialty: job?.specialtyCode,
    state: job?.state,
    city: job?.city,
    zip: job?.zip,
    shift: job?.shift,
    type: job?.type,
    weeklyPay: job?.salaryMax,
    facilityName: job?.facilityName,
    sourceType: props?.sourceType,
    sourceSubArea: props?.campaignCode,
    eligibilityPercent: eligibilityPercentageCalc(job),
  };
  Analytics.segmentTrackEvent(segmentEventName, segmentEventProps);
};

const EmailAndFavoriteIcons = ({
  // recieve from parent
  showContactAction,
  showFavoriteAction,
  recommended,
  isRecommendedJobsOnResult,
  sourceType,
  campaignCode,
  job,
  viewMyJobs,
  similarOrViewed,
  showIconsInPrimaryColor,
  onEmailClick,
  // recieve from redux store
  actionJobId,
  error,
  emailSuccess,
  emailSuccessResponse,
  allJobs,
  jobDetails,
  similarJobs,
  collaboratedJobs,
  myJobs,
  recommendedJobs,
  postTheJobApplicationFavoriteEmail,
}: EmailAndFavoriteIconsProps) => {
  const [isFavorite, setIsFavorite] = useState<boolean>(job?.isFavorite ?? false);
  const [canFavorite, setCanFavorite] = useState<boolean>(true);

  const { t } = useTranslation();

  const renderEmailFeedbackTooltip = () => {
    if (job == null) {
      return null;
    }

    return (
      <ControlledEmailTooltip
        handleEmailClick={() => onEmailClick()}
        error={error}
        success={emailSuccess}
        job={job}
        emailSuccessResponse={emailSuccessResponse}
        actionJobId={actionJobId}
        showIconsInPrimaryColor
      />
    );
  };

  const renderDefaultEmailTooltip = () => {
    if (job == null) {
      return null;
    }

    return (
      <Tooltip
        title={t("jobs.emailRecruiter.contactRecruiter")!}
        aria-label="Recruiter"
        placement="top"
      >
        <IconButton
          component="button"
          role="button"
          tabIndex={0}
          size="small"
          onKeyDown={onEmailClick}
          onClick={onEmailClick}
        >
          {job.isEmailed ? (
            <MailIcon className={`headerIcon ${styles.mailIconFilled} label-root`} />
          ) : (
            <MailOutlinedIcon
              className={`headerIcon ${
                showIconsInPrimaryColor ? styles.mailIconPrimary : styles.mailIconWhite
              } label-root`}
            />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  const getFavoriteTooltip = () => {
    if (canFavorite) {
      return isFavorite ? "Remove from Favorites" : "Add to Favorites";
    }

    return isFavorite ? "Adding..." : "Removing...";
  };

  const toggleFavorite = () => {
    const jobId = job?.id;

    if (jobId != null) {
      const newValue = !isFavorite;

      setIsFavorite(newValue);
      setCanFavorite(false);
      postTheJobApplicationFavoriteEmail({
        jobId,
        action: "favorite",
        userId: 2,
        request: {
          isActive: newValue,
        },
        jobs: allJobs,
        jobDetails,
        similarJobs,
        collaboratedJobs,
        similarOrViewed,
        recommended,
        viewMyJobs,
        myJobs,
        recommendedJobs,
        isRecommendedJobsOnResult,
      });

      const segmentEventName = t("segment.favoriteJob");

      if (newValue) trackSegmentEvent(segmentEventName, { job, sourceType, campaignCode });

      setTimeout(() => {
        setCanFavorite(true);
      }, 1000);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx(styles.actions)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {showContactAction && (
        <div className={styles.contact}>
          {(emailSuccess || error) && actionJobId === job?.id
            ? renderEmailFeedbackTooltip()
            : renderDefaultEmailTooltip()}
        </div>
      )}

      {showFavoriteAction && (
        <Tooltip title={getFavoriteTooltip()} aria-label="favorites" placement="top">
          <div className={clsx(isFavorite && styles.isFavorite, styles.favorite)}>
            <IconButton disabled={!canFavorite} onClick={toggleFavorite}>
              {isFavorite ? (
                <FavoriteIcon
                  className={clsx(
                    styles.mailIconFilled
                  )}
                />
              ) : (
                <FavoriteBorderIcon
                  className={clsx(
                    showIconsInPrimaryColor ? styles.mailIconPrimary : styles.mailIconWhite
                  )}
                />
              )}
            </IconButton>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

EmailAndFavoriteIcons.defaultProps = {
  showContactAction: false,
  showFavoriteAction: false,
  recommended: false,
  isRecommendedJobsOnResult: false,
  viewMyJobs: false,
  similarOrViewed: false,
  showIconsInPrimaryColor: false,
};

// Improve debugging (React DevTools)
EmailAndFavoriteIcons.displayName = "EmailAndFavoriteIcons";

const mapStateToProps = (state) => ({
  emailSuccess: state.emailRecruiter.emailSuccess,
  emailSuccessResponse: state.emailRecruiter.emailSuccessResponse,
  error: state.emailRecruiter.error,
  allJobs: state.jobResults.jobs,
  similarJobs: state.jobDetails.similarJobsCarouselItems,
  collaboratedJobs: state.jobDetails.collaboratedJobsCarouselItems,
  recommendedJobs: state.jobs.recommendedJobs,
  jobDetails: state.jobDetails.jobDetails,
  myJobs: state.myJobs.myJobs,
  actionJobId: state.emailRecruiter.actionJobId,
});

const mapDispatchToProps = (dispatch) => ({
  postTheJobApplicationFavoriteEmail: (reqObj) => dispatch(postJobApplicationFavoriteEmail(reqObj)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(EmailAndFavoriteIcons));
